import { ApiError } from "../api/fastapi";
import type { WorkflowStatus } from "../models/audit/analyticsEnums";

/**
 * How the UI reads the outcome of an analytics workflow call: which statuses end
 * the publish poll, and what a failed call means.
 */

/**
 * Refuse a status the caller did not handle.
 *
 * The parameter is `never`, so adding a member to `WorkflowStatus` turns every
 * non-exhaustive switch into a compile error. The throw covers the runtime case
 * a compile-time check cannot reach — a payload that got past the schema — and
 * is deliberately loud: a status silently falling through to a default arm is
 * how a column renders "not configured" while it is deleted or unentitled.
 */
export function assertUnhandledWorkflowStatus(status: never): never {
  throw new Error(`Unhandled analytics workflow status: ${String(status)}`);
}

/**
 * Whether the status poll has nothing left to observe.
 *
 * The poll exists for one thing: detecting that the workflow the auditor was
 * deep-linked out to build has become **attachable**. There is no webhook and
 * no event for that, so the signal is read off the status.
 *
 * Attachable is not the same as published, and reading publication as the
 * answer is what this function used to get wrong. It terminated as soon as
 * `publishedWorkflowUrl` turned non-null, which the backend builds for any
 * workflow with a promoted version. But AuditBoard's instance create also
 * requires the promoted version's test and review to be complete and its
 * effectiveness to be effective, and answers `422 "This workflow has not been
 * verified yet"` otherwise. So the poll declared success at publication, the
 * auditor attached, and AuditBoard refused — the poll's own signal having sent
 * them into it.
 *
 * `isVerified` is that missing half, answered by the same workflow read the
 * attach route enforces on. `null` means the backend could not establish it —
 * nothing promoted yet, or the read failed — and is treated as **keep
 * polling**, because the alternative is terminating on an unknown and leaving
 * the auditor watching a spinner that has silently stopped.
 *
 * Deliberately not `updateAvailable`: the backend's `StatusResponse` does not
 * send that field at all, so it parsed to its schema default of `false` on
 * every read and this poll could never terminate on a publication.
 *
 * Every other status is terminal for polling. Two of them are terminal because
 * their remedy is not the auditor's: a deleted workflow and a disabled analytics
 * credential will not resolve themselves, so polling them forever only burns
 * requests.
 */
export function isPublishPollTerminal(
  status: WorkflowStatus,
  publishedWorkflowUrl: string | null,
  isVerified: boolean | null = null,
): boolean {
  switch (status) {
    case "not_configured":
      return publishedWorkflowUrl !== null && isVerified === true;
    case "not_run":
    case "invalid_output":
    case "ok":
    case "workflow_deleted":
    case "not_entitled":
      return true;
    default:
      return assertUnhandledWorkflowStatus(status);
  }
}

/**
 * What went wrong with an analytics call, at the grain the banner branches on.
 *
 * The status codes disambiguate cleanly and are deliberately not collapsed:
 *
 * - `workflow_deleted` (410) — the workflow was destroyed upstream. Distinct
 *   from `not_found` because the resource still resolves; soft deletion is why.
 * - `not_found` (404) — on the configuration routes this is a mismatched id and
 *   a bug rather than a user-facing state; on the manifest reads it is the
 *   ordinary "this cell has no recorded run yet".
 * - `permission_denied` (403) — either the caller lacks the edit permission or
 *   the integration's API key lacks a workflow permission. The remedies differ
 *   and the detail names which.
 * - `conflict` (409) — the tenant's analytics credential is missing or disabled,
 *   or a concurrent binding write superseded the payload this request was
 *   composed from. Both ask the caller to read the detail.
 * - `rejected` (422, 400) — the request itself was refused: attaching a workflow
 *   with no published version, a sample-export binding naming a document, a
 *   bound key absent from the activated version's parameter schema.
 * - `session_expired` (401) - the session ended before the request was
 *   answered. Neither a permission problem nor a refusal of the request, and
 *   the remedy is to sign in again. The status carries no readable detail: the
 *   shared auth dependency answers with an object, so the copy is the
 *   surface's own.
 * - `upstream_unavailable` (502) — the workflow engine answered unusably or
 *   could not be reached. Nothing on the Midship side to fix.
 * - `unreachable` — the request died before any response arrived, so the
 *   outcome is indeterminate: a write may or may not have landed.
 * - `unreadable` — the response arrived intact and failed its schema. Alone
 *   among these kinds, no status produces it: nothing in HTTP means "the body
 *   is a well-formed answer of the wrong shape", so `failureKindForStatus`
 *   never returns it and it is thrown only at a parse site. It is separate
 *   from `unknown` so a surface can offer a reload, which retrying cannot
 *   substitute for: the server will answer the same drifted body until the
 *   page picks up a client that matches it.
 */
export const ANALYTICS_FAILURE_KINDS = [
  "workflow_deleted",
  "not_found",
  "permission_denied",
  "conflict",
  "rejected",
  "session_expired",
  "upstream_unavailable",
  "unreachable",
  "unreadable",
  "unknown",
] as const;

export type AnalyticsFailureKind = (typeof ANALYTICS_FAILURE_KINDS)[number];

/**
 * A typed analytics failure, carrying both the kind the banner switches on and
 * the status code that produced it — so a surface can distinguish two failures
 * that share a kind without the mapping having to guess which route it came
 * from.
 */
export class AnalyticsWorkflowError extends Error {
  readonly kind: AnalyticsFailureKind;
  readonly status: number;
  readonly detail: string | null;

  constructor(
    kind: AnalyticsFailureKind,
    status: number,
    detail: string | null,
  ) {
    super(detail ?? `Analytics request failed (${kind})`);
    this.name = "AnalyticsWorkflowError";
    this.kind = kind;
    this.status = status;
    this.detail = detail;
  }
}

function failureKindForStatus(
  status: number,
  isNetworkError: boolean,
): AnalyticsFailureKind {
  if (isNetworkError) return "unreachable";
  switch (status) {
    case 410:
      return "workflow_deleted";
    case 404:
      return "not_found";
    case 403:
      return "permission_denied";
    case 401:
      return "session_expired";
    case 409:
      return "conflict";
    case 400:
    case 422:
      return "rejected";
    case 502:
      return "upstream_unavailable";
    default:
      return "unknown";
  }
}

/**
 * Lift whatever a fetch rejected with into an `AnalyticsWorkflowError`.
 *
 * Every analytics fetcher funnels through this so a consumer can branch on
 * `kind` instead of re-deriving meaning from a status code, and so a raw string
 * never reaches a banner.
 */
export function toAnalyticsWorkflowError(
  error: unknown,
): AnalyticsWorkflowError {
  if (error instanceof AnalyticsWorkflowError) return error;
  if (error instanceof ApiError) {
    return new AnalyticsWorkflowError(
      failureKindForStatus(error.status, error.isNetworkError),
      error.status,
      error.detail ?? error.message,
    );
  }
  // A genuinely unexpected throw lands here, with no status to read. Not a
  // schema violation: the parse sites sit outside their request's catch and
  // throw a typed failure of their own, so a drifted payload never travels as
  // a `ZodError` message.
  return new AnalyticsWorkflowError(
    "unknown",
    0,
    error instanceof Error ? error.message : String(error),
  );
}
